import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import EventIcon from '@mui/icons-material/Event';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeProvider';
import { buildUnauthorizedUrl, isDevLoginSessionLossCode } from '../super-picker/services/api';
import { fetchMe } from './services/api';
import { buildPsfColors } from './theme';
import PsfDashboard from './pages/PsfDashboard';
import ConsumerEventsPage from './pages/ConsumerEventsPage';
import SidebarNavigation, { NavigationGroup } from '../super-picker/components/sidebar/SidebarNavigation';
import AppHeader from '../super-picker/components/header/AppHeader';
import TextSizeControl from '../super-calculator/components/TextSizeControl';
import { GlobalCalculatorSettingsProvider } from '../super-calculator/context/GlobalCalculatorSettingsContext';

type LoginUser = { name?: string; photo?: string; role?: string; email?: string; mobile?: string; ceaNumber?: string };
type PageKey = 'psf' | 'events';

const BASE_PATH = '/super-personalized-psf';

export function SuperPersonalizedPsfApp() {
  const muiTheme = useMuiTheme();
  const { currentTheme, setTheme } = useTheme();
  const C = buildPsfColors(muiTheme.palette.mode as 'dark' | 'light', muiTheme.palette);
  const location = useLocation();
  const navigate = useNavigate();

  const [accessState, setAccessState] = useState<'checking' | 'granted' | 'denied'>('checking');
  const [loginUser, setLoginUser] = useState<LoginUser | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const activePage: PageKey = location.pathname.startsWith(`${BASE_PATH}/events`) ? 'events' : 'psf';

  useEffect(() => {
    let mounted = true;
    const verify = async () => {
      try {
        const data = await fetchMe();
        if (mounted) {
          setAccessState('granted');
          setLoginUser({
            name: data.name,
            photo: data.photo,
            role: data.role,
            email: data.email,
            mobile: data.mobile,
            ceaNumber: data.ceaNumber || data.identifier,
          });
        }
      } catch (e: any) {
        const code = e?.code as string | undefined;
        const devHandled = isDevLoginSessionLossCode(code);
        if (mounted) {
          setAccessState('denied');
          if (!devHandled) {
            window.location.replace(
              buildUnauthorizedUrl(code || 'NO_SUPER_PERSONALIZED_PSF_ACCESS', { app: 'super-personalized-psf' })
            );
          }
        }
      }
    };
    verify();
    return () => { mounted = false; };
  }, []);

  const navigationGroups: NavigationGroup[] = useMemo(() => [
    {
      id: 'main',
      label: 'Menu',
      items: [
        {
          id: 'psf',
          label: 'Personalized PSF',
          icon: <QueryStatsIcon fontSize="small" />,
          active: activePage === 'psf',
          onClick: () => navigate(BASE_PATH),
        },
        {
          id: 'events',
          label: 'Consumer Event',
          icon: <EventIcon fontSize="small" />,
          active: activePage === 'events',
          onClick: () => navigate(`${BASE_PATH}/events`),
        },
      ],
    },
  ], [activePage, navigate]);

  if (accessState === 'checking') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2, bgcolor: C.surface }}>
        <CircularProgress sx={{ color: C.orange }} />
        <Typography sx={{ color: C.textMuted, fontSize: '0.85rem' }}>Verifying access…</Typography>
      </Box>
    );
  }

  if (accessState === 'denied') return null;

  return (
    <GlobalCalculatorSettingsProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: C.surface, overflow: 'hidden' }}>
      <AppHeader
        currentTheme={currentTheme}
        userInfo={loginUser ? { Name: loginUser.name, Photo: loginUser.photo } : null}
        sidebarOpen={sidebarOpen}
        onSidebarToggle={() => setSidebarOpen((open) => !open)}
        onThemeToggle={() => setTheme(currentTheme === 'dark' ? 'light' : 'dark')}
        appLabel="SUPER Personalized PSF"
        actions={<TextSizeControl />}
      />

      {/* ── Sidebar + content ── */}
      <Box sx={{ flex: 1, display: 'flex', mt: '64px', height: 'calc(100vh - 64px)', minHeight: 0, overflow: 'hidden' }}>
        <SidebarNavigation
          groups={navigationGroups}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onToggle={() => setSidebarOpen((v) => !v)}
          showMobileToggle={false}
        />
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {activePage === 'psf' ? (
            <PsfDashboard reportContext={{
              userInfo: loginUser ? {
                Name: loginUser.name,
                Photo: loginUser.photo,
                Email: loginUser.email,
                Mobile: loginUser.mobile,
                CEANumber: loginUser.ceaNumber,
              } : null,
            }} />
          ) : <ConsumerEventsPage c={C} />}
        </Box>
      </Box>
      </Box>
    </GlobalCalculatorSettingsProvider>
  );
}
